<template>
  <div class="download-form-wrapper">
    <div class="download-form">
      <div class="download-form__container">
        <div class="download-form__content">
          <!-- Loading State -->
          <div v-if="isLoading" class="download-form__state download-form__loading">
            <div class="download-form__icon-wrapper download-form__icon-wrapper--loading">
              <q-spinner-pie color="primary" size="48px" />
            </div>
            <h2 class="download-form__title">Preparing your download...</h2>
            <p class="download-form__subtitle">Please wait while we gather your files</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="download-form__state download-form__error">
            <div class="download-form__icon-wrapper download-form__icon-wrapper--error">
              <q-icon name="o_error" size="48px" color="negative" />
            </div>
            <h2 class="download-form__title">Download Unavailable</h2>
            <p class="download-form__subtitle">{{ error }}</p>
            <q-btn flat dense class="download-form__retry-btn" label="Try Again" @click="loadDownloadInfo" no-caps />
          </div>

          <!-- Expired State -->
          <div v-else-if="isExpired" class="download-form__state download-form__expired">
            <div class="download-form__icon-wrapper download-form__icon-wrapper--warning">
              <q-icon name="o_schedule" size="48px" color="warning" />
            </div>
            <h2 class="download-form__title">Link Expired</h2>
            <p class="download-form__subtitle">This download link has expired and is no longer available.</p>
          </div>

          <!-- Success State -->
          <div v-else-if="downloadInfo" class="download-form__state download-form__success">
            <!-- Welcome section -->
            <div class="download-form__welcome">
              <div class="download-form__icon-wrapper download-form__icon-wrapper--success">
                <q-icon name="o_file_download" size="48px" color="positive" />
              </div>
              <h2 class="download-form__welcome-title">Your Files Are Ready!</h2>
              <p class="download-form__welcome-subtitle">Download the selected {{ downloadInfo.fileCount }} file(s)</p>
            </div>

            <!-- Download Button -->
            <q-btn flat dense class="download-form__download-btn" @click="startDownload" no-caps>
              <q-icon name="o_file_download" class="download-form__btn-icon" />
              <span class="download-form__btn-text">Download</span>
            </q-btn>

            <!-- Expiry Info -->
            <div class="download-form__expiry-info">
              <q-icon name="o_info" size="16px" />
              <span>This link expires on {{ formatExpiryDate(downloadInfo.expiryDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Signup Section - Separate Component -->
    <SignupSection />
  </div>
</template>

<script>
import { ShareApi } from '@/api/modules/share';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import SignupSection from '@/components/share/SignupSection.vue';

export default {
  name: 'DownloadForm',

  components: {
    SignupSection
  },

  props: {
    shareId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      downloadInfo: null,
      isLoading: true,
      error: null,
      isExpired: false
    };
  },

  setup() {
    // Get the query client for potential cache management
    const queryClient = useQueryClient();

    // Create a mutation for getting share info by hash ID
    const getShareInfoMutation = useMutation({
      mutationFn: hashId => ShareApi.getShareByHashId(hashId),
      onError: error => {
        console.error('Share API error:', error);
      }
    });

    return {
      queryClient,
      getShareInfoMutation
    };
  },

  async mounted() {
    await this.loadDownloadInfo();
  },

  methods: {
    async loadDownloadInfo() {
      this.isLoading = true;
      this.error = null;
      this.isExpired = false;

      try {
        const response = await this.getShareInfoMutation.mutateAsync(this.shareId);

        if (response.data) {
          const shareData = response.data;

          // Check if the share is expired
          const now = new Date();
          const expiredDate = new Date(shareData.expired_date);
          const isExpiredByDate = now > expiredDate;
          const isExpiredByDownloadLink = !shareData.download_link;

          if (isExpiredByDate || isExpiredByDownloadLink) {
            this.isExpired = true;
            return;
          }

          // Map the API response to our expected format
          this.downloadInfo = {
            collectionName: 'Shared Files', // Generic name for shared content
            fileCount: shareData.total_files,
            downloadUrl: shareData.download_link,
            expiryDate: shareData.expired_date
          };
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Error loading share info:', error);

        if (error.response?.status === 404) {
          this.error = 'Download link not found or has been removed.';
        } else if (error.response?.status === 410) {
          this.isExpired = true;
        } else {
          this.error = error.response?.data?.message || 'Failed to load download information. Please try again.';
        }
      } finally {
        this.isLoading = false;
      }
    },

    startDownload() {
      if (!this.downloadInfo || !this.downloadInfo.downloadUrl) return;

      try {
        // Redirect to the download link to trigger download
        window.open(this.downloadInfo.downloadUrl, '_blank');

        this.$q.notify({
          type: 'positive',
          message: 'Download started successfully',
          position: 'top',
          timeout: 2000
        });
      } catch (error) {
        console.error('Error starting download:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to start download. Please try again.',
          position: 'top',
          timeout: 3000
        });
      }
    },

    formatExpiryDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.download-form-wrapper {
  flex: 1;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;

  @media (max-width: 768px) {
    max-width: none;
    width: 100%;
    padding-top: 80px;
    gap: 16px;
  }
}

.download-form {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: auto;

  @media (max-width: 768px) {
    min-height: auto;
  }

  &__container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: 768px) {
      padding: 0;
    }
  }

  &__content {
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--secondary-bg-color);
    padding: 24px;
    border-radius: 8px;

    @media (max-width: 768px) {
      border-radius: 0;
      border-left: none;
      border-right: none;
      padding: 24px 16px;
      min-height: auto;
      justify-content: flex-start;
    }
  }

  // Common state styling
  &__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }

  &__title {
    @include mona-sans-font($size: 24px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
    text-align: center;
  }

  &__subtitle {
    @include inter-font($size: 16px, $color: var(--secondary-text-color));
    margin: 0;
    text-align: center;
    line-height: 1.5;
    max-width: 300px;
  }

  // Loading State
  &__loading {
    padding: 40px 0;
  }

  // Error State
  &__error {
    padding: 40px 0;
  }

  &__icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;

    &--error {
      background-color: rgba(244, 67, 54, 0.1);
    }

    &--warning {
      background-color: rgba(255, 193, 7, 0.1);
    }

    &--loading {
      background-color: rgba(25, 118, 210, 0.1);
    }

    &--success {
      background-color: rgba(76, 175, 80, 0.1);
    }
  }

  &__retry-btn {
    width: 100%;
    height: 48px;
    background-color: var(--main-btn-color);
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    margin-top: 16px;

    &:hover {
      background-color: darken(#fecb30, 5%);
    }
  }

  // Expired State
  &__expired {
    padding: 40px 0;
  }

  // Success State
  &__success {
    padding: 20px 0;
  }

  &__welcome {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }

  &__welcome-title {
    @include mona-sans-font($size: 24px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
    text-align: center;
  }

  &__welcome-subtitle {
    @include inter-font($size: 18px, $color: var(--secondary-text-color));
    margin: 0 0 16px 0;
    text-align: center;
  }

  &__download-btn {
    width: 100%;
    height: 48px;
    background-color: var(--main-btn-color);
    border-radius: 8px;
    @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
  }

  &__btn-icon {
    font-size: 20px;
  }

  &__btn-text {
    @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
    margin-left: 12px;
  }

  &__expiry-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 0 16px;
    height: 48px;
    width: 100%;
    background-color: var(--terziary-btn-color);
    border-radius: 8px;
    @include inter-font($size: 14px, $color: var(--main-text-color));
    box-sizing: border-box;

    .q-icon {
      color: var(--main-text-color);
    }
  }
}

// Responsive improvements
@media (max-width: 480px) {
  .download-form {
    &__welcome-title {
      font-size: 20px;
    }

    &__count-number {
      font-size: 28px;
    }

    &__download-btn {
      font-size: 14px;
    }
  }
}
</style>
